import {
    Box,
    Typography,
    Slider,
    Paper,
    Button,
    IconButton,
    Tooltip
} from '@mui/material';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import React from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { t } from 'i18next';

export const HeartRateCalculator = ({
    birthDate,
    restingHeartRate,
    maxHeartRate,
    setFieldValue
}) => {
    const age = birthDate instanceof Date ? dayjs().diff(dayjs(birthDate), 'year') : null;
    const computedMaxHR = age ? 220 - age : null;

    const maxHRnum = maxHeartRate ? Number(maxHeartRate) : computedMaxHR;

    const restingHRnum = restingHeartRate ? Number(restingHeartRate) : null;

    const [intensityRange, setIntensityRange] = React.useState([60, 70]);

    const karvonenTraining =
        maxHRnum && restingHRnum
            ? Math.round(
                  (maxHRnum - restingHRnum) * (intensityRange[1] / 100) + restingHRnum
              )
            : null;

    const karvonenMinTraining =
        maxHRnum && restingHRnum
            ? Math.round(
                  (maxHRnum - restingHRnum) * (intensityRange[0] / 100) + restingHRnum
              )
            : null;

    const handleCopyValues = () => {
        if (!setFieldValue) return;

        setFieldValue('maxHeartRate', maxHRnum || '');
        setFieldValue('trainingHeartRate', karvonenTraining || '');
        setFieldValue('minTrainingHeartRate', karvonenMinTraining || '');
    };

    return (
        <Paper sx={{ p: 2, borderRadius: 2, backgroundColor: '#f9f9f9' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h4" fontWeight="bold" color="#1976d2">
                    {t('calculator.title')}
                </Typography>
                <Tooltip title={t('calculator.tooltipSuggested')} arrow>
                    <IconButton
                        onClick={handleCopyValues}
                        sx={{
                            backgroundColor: '#0A579B',
                            color: '#fff',
                            '&:hover': { backgroundColor: '#084472' }
                        }}
                    >
                        <ContentCopyIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <Box mb={2}>
                <Typography variant="body1">
                    {t('calculator.maxHR')}{' '}
                    <strong>{maxHRnum ? `${maxHRnum} bpm` : '—'}</strong>
                </Typography>
            </Box>

            <Box mb={2}>
                <Typography variant="body1">
                    {t('calculator.trainingHR')}{' '}
                    <strong>{karvonenTraining ? `${karvonenTraining} bpm` : '—'}</strong>
                </Typography>

                <Typography variant="body2" color="text.secondary">
                    {t('calculator.minTrainingHR')}{' '}
                    <strong>
                        {karvonenMinTraining ? `${karvonenMinTraining} bpm` : '—'}
                    </strong>
                </Typography>
            </Box>

            <Box mb={2}>
                <Typography variant="body1" gutterBottom>
                    {t('calculator.intensity')}: {intensityRange[0]}% –{' '}
                    {intensityRange[1]}%
                </Typography>

                <Slider
                    value={intensityRange}
                    onChange={(e, val) => setIntensityRange(val)}
                    step={5}
                    min={40}
                    max={100}
                    valueLabelDisplay="auto"
                    disableSwap
                />
            </Box>
        </Paper>
    );
};

HeartRateCalculator.propTypes = {
    birthDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    restingHeartRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    maxHeartRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setFieldValue: PropTypes.func
};
